import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/docs_/products")({
  component: ProductsDocsPage,
});

function ProductsDocsPage() {
  return (
    <div className="max-w-4xl mx-auto bg-[#F5F5F5] -m-6 p-8 min-h-screen">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm" style={{ color: "#A9A9A9" }}>
            <li>
              <Link to="/docs" className="hover:text-[#171717] transition-colors">
                Documentation
              </Link>
            </li>
            <li>/</li>
            <li style={{ color: "#171717" }}>Products</li>
          </ol>
        </nav>

        <h1 className="text-4xl mb-6" style={{ color: "#171717", fontWeight: 300, lineHeight: 1.1 }}>
          Managing Products
        </h1>

        <div className="space-y-8">
          <p className="text-lg mb-8" style={{ color: "#171717", fontWeight: 300, lineHeight: 1.4 }}>
            Learn how to import, manage, and enrich your product catalogue using
            the Product Intelligence Cloud.
          </p>

          {/* Importing Products */}
          <section className="mb-12">
            <h2 className="text-2xl mb-6" style={{ color: "#171717", fontWeight: 300 }}>
              Importing Products
            </h2>

            <div className=" p-6 mb-6" style={{ backgroundColor: "#FFFFFF" }}>
              <h3 className="text-lg mb-3" style={{ color: "#171717", fontWeight: 300 }}>CSV Import</h3>
              <p className="mb-4" style={{ color: "#171717", fontWeight: 300 }}>
                The CSV import wizard supports automatic column detection and
                manual mapping. Required columns:
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: "1px solid #E5E5E5" }}>
                      <th className="text-left py-2 px-3" style={{ color: "#A9A9A9", fontWeight: 300 }}>Column</th>
                      <th className="text-left py-2 px-3" style={{ color: "#A9A9A9", fontWeight: 300 }}>Required</th>
                      <th className="text-left py-2 px-3" style={{ color: "#A9A9A9", fontWeight: 300 }}>Description</th>
                    </tr>
                  </thead>
                  <tbody style={{ color: "#171717" }}>
                    {[
                      { col: "title", req: "Yes", desc: "Product name" },
                      { col: "sku", req: "No", desc: "Unique product identifier" },
                      { col: "brand", req: "No", desc: "Brand or manufacturer name" },
                      { col: "category", req: "No", desc: "Product category" },
                      { col: "price", req: "No", desc: "Numeric price (currency symbols removed)" },
                      { col: "description", req: "No", desc: "Product description" },
                      { col: "imageUrl", req: "No", desc: "URL to product image" },
                    ].map(({ col, req, desc }) => (
                      <tr key={col} style={{ borderBottom: "1px solid #E5E5E5" }}>
                        <td className="py-2 px-3"><code style={{ color: "#3941EB", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>{col}</code></td>
                        <td className="py-2 px-3" style={{ fontWeight: 300 }}>{req === "Yes" ? "✓ Yes" : "No"}</td>
                        <td className="py-2 px-3" style={{ fontWeight: 300 }}>{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className=" p-6" style={{ backgroundColor: "#FFFFFF" }}>
              <h3 className="text-lg mb-3" style={{ color: "#171717", fontWeight: 300 }}>Shopify Sync</h3>
              <p className="mb-4" style={{ color: "#171717", fontWeight: 300 }}>
                Connect your Shopify store to automatically import products. The sync will:
              </p>
              <ul className="list-disc list-inside space-y-2" style={{ color: "#171717", fontWeight: 300 }}>
                <li>Import all active products from your store</li>
                <li>Map Shopify product fields to PIC schema</li>
                <li>Strip HTML from descriptions</li>
                <li>Handle product variants</li>
                <li>Update existing products on re-sync</li>
              </ul>
            </div>
          </section>

          {/* Product Status */}
          <section className="mb-12">
            <h2 className="text-2xl mb-6" style={{ color: "#171717", fontWeight: 300 }}>
              Product Status
            </h2>

            <div className="grid gap-4 md:grid-cols-3">
              <div className=" p-5" style={{ backgroundColor: "#FFFFFF" }}>
                <span className="inline-block px-3 py-1 rounded-full text-xs uppercase mb-3" style={{ backgroundColor: "#00FBA9", color: "#171717", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>
                  ACTIVE
                </span>
                <p className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>
                  Product is visible and can be enriched via APIs
                </p>
              </div>
              <div className=" p-5" style={{ backgroundColor: "#FFFFFF" }}>
                <span className="inline-block px-3 py-1 rounded-full text-xs uppercase mb-3" style={{ backgroundColor: "#FFB788", color: "#171717", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>
                  DRAFT
                </span>
                <p className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>
                  Product is being prepared and not yet visible
                </p>
              </div>
              <div className=" p-5" style={{ backgroundColor: "#FFFFFF" }}>
                <span className="inline-block px-3 py-1 rounded-full text-xs uppercase mb-3" style={{ backgroundColor: "#E5E5E5", color: "#171717", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>
                  ARCHIVED
                </span>
                <p className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>
                  Product has been archived and is hidden
                </p>
              </div>
            </div>
          </section>

          {/* Embedding Status */}
          <section className="mb-12">
            <h2 className="text-2xl mb-6" style={{ color: "#171717", fontWeight: 300 }}>
              Embedding Status
            </h2>

            <div className=" p-6" style={{ backgroundColor: "#FFFFFF" }}>
              <p className="mb-4" style={{ color: "#171717", fontWeight: 300 }}>
                Products can be enriched with AI-generated embeddings for
                semantic search. The embedding status shows the processing state:
              </p>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="inline-block px-3 py-1 rounded-full text-xs uppercase" style={{ backgroundColor: "#E5E5E5", color: "#171717", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>PENDING</span>
                  <span className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>Embeddings not yet generated</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-block px-3 py-1 rounded-full text-xs uppercase" style={{ backgroundColor: "#9EA8FF", color: "#171717", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>PROCESSING</span>
                  <span className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>Embeddings are being generated</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-block px-3 py-1 rounded-full text-xs uppercase" style={{ backgroundColor: "#00FBA9", color: "#171717", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>COMPLETE</span>
                  <span className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>Product has 768-dimensional embeddings for search</span>
                </div>
              </div>
            </div>
          </section>

          {/* API Access */}
          <section className="mb-12">
            <h2 className="text-2xl mb-6" style={{ color: "#171717", fontWeight: 300 }}>
              API Access
            </h2>

            <div className=" p-6" style={{ backgroundColor: "#FFFFFF" }}>
              <p className="mb-4" style={{ color: "#171717", fontWeight: 300 }}>
                Access product data programmatically using the REST API:
              </p>

              <div className="space-y-4">
                <div className=" p-4" style={{ backgroundColor: "#171717" }}>
                  <p className="text-xs mb-2 uppercase tracking-wide" style={{ color: "#A9A9A9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>List all products</p>
                  <code style={{ color: "#00FBA9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>GET /api/products?page=1&limit=20</code>
                </div>

                <div className=" p-4" style={{ backgroundColor: "#171717" }}>
                  <p className="text-xs mb-2 uppercase tracking-wide" style={{ color: "#A9A9A9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>Get single product</p>
                  <code style={{ color: "#00FBA9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>GET /api/products/:id</code>
                </div>

                <div className=" p-4" style={{ backgroundColor: "#171717" }}>
                  <p className="text-xs mb-2 uppercase tracking-wide" style={{ color: "#A9A9A9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>Search products</p>
                  <code style={{ color: "#00FBA9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>GET /api/products?search=gucci&category=handbags</code>
                </div>
              </div>
            </div>
          </section>

          {/* Navigation */}
          <div className="flex justify-between pt-6" style={{ borderTop: "1px solid #E5E5E5" }}>
            <Link to="/docs/getting-started" style={{ color: "#3941EB", fontWeight: 300 }}>
              ← Getting Started
            </Link>
            <Link to="/docs" style={{ color: "#3941EB", fontWeight: 300 }}>
              API Reference →
            </Link>
          </div>
        </div>
      </div>
  );
}

